// src/components/Tebleros/Campo/TableroFieldEvents.js
import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Typography,
  Snackbar,
  Box,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Paper,
  Stack,
  Chip,
  Divider,
  FormGroup,
  Alert,
  useTheme,
  useMediaQuery,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Save as SaveIcon,
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  FormatSize as FormatSizeIcon,
  ColorLens as ColorLensIcon,
  RestartAlt as RestartAltIcon,
  Style as StyleIcon,
  ViewWeek as ViewWeekIcon,
} from '@mui/icons-material';
import { useDebounce } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import socketSyncService from '../../../services/SocketSyncService';
import HorizontalEventTable from './Tablas/HorizontalEventTable';
import VerticalEventTable from './Tablas/VerticalEventTable';

// Panel de pestañas para la configuración
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const TableroFieldEvents = ({
  configurations = [],
  setConfigurations = () => {},
  openTablero,
  openWindows: propOpenWindows,
  setOpenWindows: propSetOpenWindows,
}) => {
  // Estado para las pestañas
  const [localOpenWindows, setLocalOpenWindows] = useState({});
  
  // Usa las props si están disponibles, de lo contrario usa estado local
  const openWindows = propOpenWindows || localOpenWindows;
  const setOpenWindows = propSetOpenWindows || setLocalOpenWindows;
  const [activeTab, setActiveTab] = useState(0);
  
  // Socket connection state
  const [isConnected, setIsConnected] = useState(false);
  
  // Estados para detalles del evento
  const [eventDetails, setEventDetails] = useState({
    eventName: 'No hay eventos seleccionados',
    eventSubtitle: '',
    event: '',
    round: '',
    heat: '',
  });
  const [results, setResults] = useState([]);

  // Tipo de evento de campo
  const [eventType, setEventType] = useState('horizontal'); // 'horizontal' o 'vertical'
  
  // Opciones de visualización
  const [showAttemptNumber, setShowAttemptNumber] = useState(true);
  const [displayRows, setDisplayRows] = useState(8);

  // Dimensiones
  const [tableWidth, setTableWidth] = useState(1000);
  const [tableHeight, setTableHeight] = useState(800);

  // Colores
  const [tableBackgroundColor, setTableBackgroundColor] = useState('#1a1a1a');
  const [globalFontColor, setGlobalFontColor] = useState('#ffffff');
  const [headerColor, setHeaderColor] = useState('#2c0369');
  const [positionColor, setPositionColor] = useState('#4c516d');
  const [nameBackgroundColor, setNameBackgroundColor] = useState('#515e7b');
  const [markBackgroundColor, setMarkBackgroundColor] = useState('#151515');
  const [headerFontColor, setHeaderFontColor] = useState('#ffffff');
  const [athleteNameColor, setAthleteNameColor] = useState('#ffffff');
  const [teamNameColor, setTeamNameColor] = useState('#e0e0e0');
  const [resultColor, setResultColor] = useState('#ffffff');
  
  // Colores para intentos en pruebas verticales
  const [successColor, setSuccessColor] = useState('#1b5e20');
  const [failColor, setFailColor] = useState('#b71c1c');
  const [passColor, setPassColor] = useState('#0d47a1');

  // Tamaños de fuente
  const [headerFontSize, setHeaderFontSize] = useState(24);
  const [positionFontSize, setPositionFontSize] = useState(48);
  const [nameFontSize, setNameFontSize] = useState(36);
  const [teamFontSize, setTeamFontSize] = useState(24);
  const [resultFontSize, setResultFontSize] = useState(54);
  const [attemptFontSize, setAttemptFontSize] = useState(32);

  // Snackbar para feedback visual
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Estado para configuración actual
  const [currentConfig, setCurrentConfig] = useState({});
  const [debouncedConfig] = useDebounce(currentConfig, 500);

  // Estado para seleccionar una configuración
  const [selectedConfig, setSelectedConfig] = useState('');
  const [configName, setConfigName] = useState('');

  // Temas predefinidos
  const themes = {
    atletismo: {
      headerColor: '#2c0369',
      positionColor: '#4c516d',
      nameBackgroundColor: '#515e7b',
      markBackgroundColor: '#151515',
      tableBackgroundColor: '#1a1a1a',
      globalFontColor: '#ffffff',
      headerFontColor: '#ffffff',
      athleteNameColor: '#ffffff',
      teamNameColor: '#e0e0e0',
      resultColor: '#ffffff',
      successColor: '#1b5e20',
      failColor: '#b71c1c',
      passColor: '#0d47a1',
    },
    indoor: {
      headerColor: '#1a237e',
      positionColor: '#303f9f',
      nameBackgroundColor: '#3949ab',
      markBackgroundColor: '#121212',
      tableBackgroundColor: '#0d0d0d',
      globalFontColor: '#ffffff',
      headerFontColor: '#ffffff',
      athleteNameColor: '#ffffff',
      teamNameColor: '#bbdefb',
      resultColor: '#ffeb3b',
      successColor: '#00c853',
      failColor: '#d50000',
      passColor: '#2962ff',
    },
    olímpico: {
      headerColor: '#00796b',
      positionColor: '#006064',
      nameBackgroundColor: '#00838f',
      markBackgroundColor: '#004d40',
      tableBackgroundColor: '#002419',
      globalFontColor: '#ffffff',
      headerFontColor: '#ffffff',
      athleteNameColor: '#ffffff',
      teamNameColor: '#b2dfdb',
      resultColor: '#ffd54f',
      successColor: '#00e676',
      failColor: '#ff1744',
      passColor: '#00b0ff',
    },
    claro: {
      headerColor: '#1565c0',
      positionColor: '#1976d2',
      nameBackgroundColor: '#1e88e5',
      markBackgroundColor: '#e3f2fd',
      tableBackgroundColor: '#ffffff',
      globalFontColor: '#212121',
      headerFontColor: '#ffffff',
      athleteNameColor: '#ffffff',
      teamNameColor: '#e1f5fe',
      resultColor: '#212121',
      successColor: '#2e7d32',
      failColor: '#c62828',
      passColor: '#0d47a1',
    },
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Valores predeterminados para restauración
  const defaultSettings = {
    eventType: 'horizontal',
    showAttemptNumber: true,
    displayRows: 8,
    tableWidth: 1000,
    tableHeight: 800,
    tableBackgroundColor: '#1a1a1a',
    globalFontColor: '#ffffff',
    headerColor: '#2c0369',
    positionColor: '#4c516d',
    nameBackgroundColor: '#515e7b',
    markBackgroundColor: '#151515',
    headerFontColor: '#ffffff',
    athleteNameColor: '#ffffff',
    teamNameColor: '#e0e0e0',
    resultColor: '#ffffff',
    successColor: '#1b5e20',
    failColor: '#b71c1c',
    passColor: '#0d47a1',
    headerFontSize: 24,
    positionFontSize: 48,
    nameFontSize: 36,
    teamFontSize: 24,
    resultFontSize: 54,
    attemptFontSize: 32,
  };

  const resetToDefault = () => {
    setEventType(defaultSettings.eventType);
    setShowAttemptNumber(defaultSettings.showAttemptNumber);
    setDisplayRows(defaultSettings.displayRows);
    setTableWidth(defaultSettings.tableWidth);
    setTableHeight(defaultSettings.tableHeight);
    setTableBackgroundColor(defaultSettings.tableBackgroundColor);
    setGlobalFontColor(defaultSettings.globalFontColor);
    setHeaderColor(defaultSettings.headerColor);
    setPositionColor(defaultSettings.positionColor);
    setNameBackgroundColor(defaultSettings.nameBackgroundColor);
    setMarkBackgroundColor(defaultSettings.markBackgroundColor);
    setHeaderFontColor(defaultSettings.headerFontColor);
    setAthleteNameColor(defaultSettings.athleteNameColor);
    setTeamNameColor(defaultSettings.teamNameColor);
    setResultColor(defaultSettings.resultColor);
    setSuccessColor(defaultSettings.successColor);
    setFailColor(defaultSettings.failColor);
    setPassColor(defaultSettings.passColor);
    setHeaderFontSize(defaultSettings.headerFontSize);
    setPositionFontSize(defaultSettings.positionFontSize);
    setNameFontSize(defaultSettings.nameFontSize);
    setTeamFontSize(defaultSettings.teamFontSize);
    setResultFontSize(defaultSettings.resultFontSize);
    setAttemptFontSize(defaultSettings.attemptFontSize);

    showSnackbar('Valores restaurados a los predeterminados', 'success');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Aplicar tema
  const applyTheme = (themeName) => {
    const themeValues = themes[themeName];
    if (!themeValues) return;

    setHeaderColor(themeValues.headerColor);
    setPositionColor(themeValues.positionColor);
    setNameBackgroundColor(themeValues.nameBackgroundColor);
    setMarkBackgroundColor(themeValues.markBackgroundColor);
    setTableBackgroundColor(themeValues.tableBackgroundColor);
    setGlobalFontColor(themeValues.globalFontColor);
    setHeaderFontColor(themeValues.headerFontColor);
    setAthleteNameColor(themeValues.athleteNameColor);
    setTeamNameColor(themeValues.teamNameColor);
    setResultColor(themeValues.resultColor);
    setSuccessColor(themeValues.successColor);
    setFailColor(themeValues.failColor);
    setPassColor(themeValues.passColor);

    showSnackbar(`Tema "${themeName}" aplicado correctamente`, 'success');
  };

  // Función para abrir el tablero con la configuración actual
  const openCurrentTablero = () => {
    const configId = 'currentFieldConfig';
    
    // Guarda la configuración actual
    const currentConfigToSave = {
      id: configId,
      name: eventDetails.eventName !== 'No hay eventos seleccionados' 
          ? eventDetails.eventName 
          : `Configuración de ${eventType === 'horizontal' ? 'Horizontal' : 'Vertical'} Actual`,
      eventData: eventDetails,
      settings: {
        eventType,
        showAttemptNumber,
        displayRows,
        tableWidth,
        tableHeight,
        tableBackgroundColor,
        globalFontColor,
        headerColor,
        positionColor,
        nameBackgroundColor,
        markBackgroundColor,
        headerFontColor,
        athleteNameColor,
        teamNameColor,
        resultColor,
        successColor,
        failColor,
        passColor,
        headerFontSize,
        positionFontSize,
        nameFontSize,
        teamFontSize,
        resultFontSize,
        attemptFontSize,
      },
    };
    
    localStorage.setItem(`config-${configId}`, JSON.stringify(currentConfigToSave));
      
    // Abrir la ventana con dimensiones adecuadas
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const width = Math.min(tableWidth + 40, screenWidth);
    const height = Math.min(tableHeight + 40, screenHeight);
    
    // Usar una ruta basada en el tipo de evento
    const path = eventType === 'horizontal' ? 
      `/horizontal-scoreboard?id=${configId}` : 
      `/vertical-scoreboard?id=${configId}`;
    
      const newWindow = window.open(
        path, // CORRECTO - Usa la variable 'path' que ya definiste
        `tablero-${configId}`, 
        `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`
      );
    
    if (newWindow && setOpenWindows) {
      setOpenWindows((prevWindows) => ({ ...prevWindows, [configId]: newWindow }));
    } else if (!newWindow) {
      alert('No se pudo abrir la ventana. Por favor, permite las ventanas emergentes en tu navegador.');
    }
  };

  // Función para guardar la configuración actual
  const saveConfiguration = () => {
    if (!configName.trim()) {
      showSnackbar('El nombre de la configuración no puede estar vacío', 'error');
      return;
    }

    const newConfig = {
      id: uuidv4(),
      name: configName,
      type: eventType === 'horizontal' ? 'field_horizontal' : 'field_vertical', // Distinto tipo según evento
      settings: {
        eventType,
        showAttemptNumber,
        displayRows,
        tableWidth,
        tableHeight,
        tableBackgroundColor,
        globalFontColor,
        headerColor,
        positionColor,
        nameBackgroundColor,
        markBackgroundColor,
        headerFontColor,
        athleteNameColor,
        teamNameColor,
        resultColor,
        successColor,
        failColor,
        passColor,
        headerFontSize,
        positionFontSize,
        nameFontSize,
        teamFontSize,
        resultFontSize,
        attemptFontSize,
      },
    };

    const updatedConfigs = [...configurations, newConfig];
    setConfigurations(updatedConfigs);
    localStorage.setItem('configurations', JSON.stringify(updatedConfigs));
    showSnackbar('Configuración guardada');
    setConfigName('');
  };

  // Cargar una configuración
  const loadConfiguration = (configId) => {
    const configToLoad = configurations.find((config) => config.id === configId);
    if (configToLoad && configToLoad.settings) {
      const settings = configToLoad.settings;
      
      // Cargar todos los valores de la configuración
      setEventType(settings.eventType || defaultSettings.eventType);
      setShowAttemptNumber(settings.showAttemptNumber !== undefined ? settings.showAttemptNumber : defaultSettings.showAttemptNumber);
      setDisplayRows(settings.displayRows || defaultSettings.displayRows);
      setTableWidth(settings.tableWidth || defaultSettings.tableWidth);
      setTableHeight(settings.tableHeight || defaultSettings.tableHeight);
      setTableBackgroundColor(settings.tableBackgroundColor || defaultSettings.tableBackgroundColor);
      setGlobalFontColor(settings.globalFontColor || defaultSettings.globalFontColor);
      setHeaderColor(settings.headerColor || defaultSettings.headerColor);
      setPositionColor(settings.positionColor || defaultSettings.positionColor);
      setNameBackgroundColor(settings.nameBackgroundColor || defaultSettings.nameBackgroundColor);
      setMarkBackgroundColor(settings.markBackgroundColor || defaultSettings.markBackgroundColor);
      setHeaderFontColor(settings.headerFontColor || defaultSettings.headerFontColor);
      setAthleteNameColor(settings.athleteNameColor || defaultSettings.athleteNameColor);
      setTeamNameColor(settings.teamNameColor || defaultSettings.teamNameColor);
      setResultColor(settings.resultColor || defaultSettings.resultColor);
      setSuccessColor(settings.successColor || defaultSettings.successColor);
      setFailColor(settings.failColor || defaultSettings.failColor);
      setPassColor(settings.passColor || defaultSettings.passColor);
      setHeaderFontSize(settings.headerFontSize || defaultSettings.headerFontSize);
      setPositionFontSize(settings.positionFontSize || defaultSettings.positionFontSize);
      setNameFontSize(settings.nameFontSize || defaultSettings.nameFontSize);
      setTeamFontSize(settings.teamFontSize || defaultSettings.teamFontSize);
      setResultFontSize(settings.resultFontSize || defaultSettings.resultFontSize);
      setAttemptFontSize(settings.attemptFontSize || defaultSettings.attemptFontSize);

      // Guardar en localStorage para sincronización
      localStorage.setItem(`config-${configId}`, JSON.stringify(configToLoad));

      showSnackbar(`Configuración "${configToLoad.name}" cargada`);
    }
  };

  // Actualizar el currentConfig cuando cambian las configuraciones
  useEffect(() => {
    setCurrentConfig({
      settings: {
        eventType,
        showAttemptNumber,
        displayRows,
        tableWidth,
        tableHeight,
        tableBackgroundColor,
        globalFontColor,
        headerColor,
        positionColor,
        nameBackgroundColor,
        markBackgroundColor,
        headerFontColor,
        athleteNameColor,
        teamNameColor,
        resultColor,
        successColor,
        failColor,
        passColor,
        headerFontSize,
        positionFontSize,
        nameFontSize,
        teamFontSize,
        resultFontSize,
        attemptFontSize,
      },
    });
  }, [
    eventType,
    showAttemptNumber,
    displayRows,
    tableWidth,
    tableHeight,
    tableBackgroundColor,
    globalFontColor,
    headerColor,
    positionColor,
    nameBackgroundColor,
    markBackgroundColor,
    headerFontColor,
    athleteNameColor,
    teamNameColor,
    resultColor,
    successColor,
    failColor,
    passColor,
    headerFontSize,
    positionFontSize,
    nameFontSize,
    teamFontSize,
    resultFontSize,
    attemptFontSize,
  ]);

  // Guardar currentConfig en localStorage cuando cambia
  useEffect(() => {
    if (debouncedConfig.settings) {
      localStorage.setItem('currentFieldConfiguration', JSON.stringify(debouncedConfig));
    }
  }, [debouncedConfig]);

  // Actualizar estado de conexión
  useEffect(() => {
    // Función que simula actualización del estado de conexión
    const updateConnectionStatus = () => {
      // En un entorno real, esto se conectaría a tu servicio de socket
      setIsConnected(socketSyncService.getConnectionStatus()?.isConnected || false);
    };
    
    updateConnectionStatus();
    
    // En un sistema real, registrarías eventos de conexión/desconexión
    return () => {
      // Limpieza cuando el componente se desmonta
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Panel de control */}
      <Grid container spacing={3}>
        {/* Estado de conexión */}
        <Grid item xs={12}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: isConnected ? 'rgba(46, 125, 50, 0.08)' : 'rgba(211, 47, 47, 0.08)',
              mb: 2
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body1" color={isConnected ? 'success.main' : 'error.main'}>
                {isConnected ? 'Conectado al servidor de eventos' : 'Desconectado del servidor'}
              </Typography>
              <Chip 
                size="small"
                color={isConnected ? 'success' : 'error'}
                label={isConnected ? 'Online' : 'Offline'}
              />
            </Stack>
            <Button 
              startIcon={<RefreshIcon />}
              variant="outlined"
              color={isConnected ? 'success' : 'error'}
              size="small"
              onClick={() => {
                if (!isConnected) {
                  const result = socketSyncService.reconnect?.() || false;
                  if (result) {
                    showSnackbar('Intentando reconectar...', 'info');
                  } else {
                    showSnackbar('No se pudo reconectar al servidor', 'warning');
                  }
                }
              }}
              disabled={isConnected}
            >
              {isConnected ? 'Conectado' : 'Reconectar'}
            </Button>
          </Paper>
        </Grid>

        {/* Detalles del evento actual */}
        <Grid item xs={12}>
          <Card elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader 
              title={
                <Typography variant="h5" fontWeight="bold">
                  {eventDetails.eventName || 'No hay eventos seleccionados'}
                </Typography>
              }
              subheader={
                <Stack 
                  direction={isMobile ? "column" : "row"} 
                  spacing={2} 
                  alignItems={isMobile ? "flex-start" : "center"}
                  sx={{ mt: 1 }}
                >
                  {eventDetails.event && (
                    <Chip
                      size="small"
                      label={`Evento: ${eventDetails.event}`}
                      variant="outlined"
                      color="primary"
                    />
                  )}
                  {eventDetails.round && (
                    <Chip
                      size="small"
                      label={`Ronda: ${eventDetails.round}`}
                      variant="outlined"
                      color="secondary"
                    />
                  )}
                  {eventDetails.heat && (
                    <Chip
                      size="small"
                      label={`Heat: ${eventDetails.heat}`}
                      variant="outlined"
                      color="info"
                    />
                  )}
                </Stack>
              }
              action={
                <Box sx={{ p: 1 }}>
                  <Chip 
                    label={eventType === 'horizontal' ? 'Horizontal' : 'Vertical'} 
                    color="primary" 
                  />
                </Box>
              }
            />
          </Card>
        </Grid>

        {/* Panel de configuración con tabs */}
        <Grid item xs={12}>
          <Card elevation={3} sx={{ borderRadius: 2, overflow: 'visible' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant={isMobile ? "scrollable" : "fullWidth"}
                scrollButtons={isMobile ? "auto" : false}
                allowScrollButtonsMobile
                aria-label="configuración de tablero de campo"
              >
                <Tab 
                  icon={<SettingsIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="General" 
                />
                <Tab 
                  icon={<ViewWeekIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Evento" 
                />
                <Tab 
                  icon={<FormatSizeIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Tipografía" 
                />
                <Tab 
                  icon={<ColorLensIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Colores" 
                />
                <Tab 
                  icon={<StyleIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Temas" 
                />
                <Tab 
                  icon={<SaveIcon fontSize="small" />} 
                  iconPosition="start" 
                  label="Presets" 
                />
              </Tabs>
            </Box>

            {/* Tab 1: Configuración General */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 320 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Dimensiones del Tablero
                    </Typography>
                    <Stack spacing={3}>
                    <TextField
                      label="Filas Visibles"
                      type="number"
                      value={displayRows}
                      onChange={(e) => setDisplayRows(parseInt(e.target.value) || 1)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1 }}
                      fullWidth
                    />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          1 fila: Vista detallada de un atleta. 2+ filas: Lista de múltiples atletas.
                        </Typography>
                      <TextField
                        label="Ancho (px)"
                        type="number"
                        value={tableWidth}
                        onChange={(e) => setTableWidth(parseInt(e.target.value) || 0)}
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 100 }}
                        fullWidth
                      />
                      <TextField
                        label="Altura (px)"
                        type="number"
                        value={tableHeight}
                        onChange={(e) => setTableHeight(parseInt(e.target.value) || 0)}
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 100 }}
                        fullWidth
                      />
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 320 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Acciones
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<OpenInNewIcon />}
                        onClick={openCurrentTablero}
                        sx={{ mb: 2 }}
                      >
                        Abrir Tablero
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={resetToDefault}
                        sx={{ mb: 2 }}
                      >
                        Restaurar Valores
                      </Button>
                    </Stack>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ mt: 4, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Vista Previa del Tamaño
                      </Typography>
                      <Box sx={{ 
                        width: '100%', 
                        height: '120px', 
                        border: '2px dashed #ccc', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{
                          position: 'absolute',
                          width: `${Math.min(100, (tableWidth / 1200) * 100)}%`,
                          height: `${Math.min(100, (tableHeight / 800) * 100)}%`,
                          backgroundColor: 'rgba(25, 118, 210, 0.2)',
                          border: '2px solid #1976d2',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" fontWeight="bold">
                            {tableWidth} × {tableHeight} px
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Los cambios se guardan automáticamente y se aplicarán al abrir un nuevo tablero.
                    </Alert>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 2: Configuración de Evento */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Tipo de Evento
                    </Typography>
                    <RadioGroup
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      name="event-type-group"
                    >
                      <FormControlLabel 
                        value="horizontal" 
                        control={<Radio />} 
                        label="Horizontal (Lanzamiento, Salto Largo, Triple)" 
                      />
                      <FormControlLabel 
                        value="vertical" 
                        control={<Radio />} 
                        label="Vertical (Salto Alto, Salto con Pértiga)" 
                      />
                    </RadioGroup>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Configuración específica para {eventType === 'horizontal' ? 'Eventos Horizontales' : 'Eventos Verticales'}
                    </Typography>
                    
                    {eventType === 'horizontal' ? (
                      /* Configuraciones específicas para eventos horizontales */
                      <Box>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showAttemptNumber}
                                onChange={(e) => setShowAttemptNumber(e.target.checked)}
                                name="showAttemptNumber"
                              />
                            }
                            label="Mostrar Número de Intento"
                          />
                        </FormGroup>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="body2" color="text.secondary" paragraph>
                          En eventos horizontales como lanzamientos o salto largo, los resultados se muestran como distancias
                          y pueden tener un número de intento visible.
                        </Typography>
                        
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                          borderRadius: 1,
                          mt: 2
                        }}>
                          <Typography fontFamily="monospace">
                            7.25m (+1.6 m/s)
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      /* Configuraciones específicas para eventos verticales */
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Colores de Intentos
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: failColor }}>X</Typography>
                            <Typography variant="body2">Fallido</Typography>
                            <TextField
                              type="color"
                              size="small"
                              value={failColor}
                              onChange={(e) => setFailColor(e.target.value)}
                              sx={{ ml: 'auto', width: '80px' }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: passColor }}>P</Typography>
                            <Typography variant="body2">Pasa</Typography>
                            <TextField
                              type="color"
                              size="small"
                              value={passColor}
                              onChange={(e) => setPassColor(e.target.value)}
                              sx={{ ml: 'auto', width: '80px' }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: successColor }}>O</Typography>
                            <Typography variant="body2">Exitoso</Typography>
                            <TextField
                              type="color"
                              size="small"
                              value={successColor}
                              onChange={(e) => setSuccessColor(e.target.value)}
                              sx={{ ml: 'auto', width: '80px' }}
                            />
                          </Box>
                        </Stack>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="body2" color="text.secondary" paragraph>
                          En eventos verticales como salto alto o salto con pértiga, los resultados incluyen 
                          la altura alcanzada y el estado de los intentos (X: Fallo, P: Pasa, O: Exitoso).
                        </Typography>
                        
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                          borderRadius: 1,
                          mt: 2
                        }}>
                          <Stack direction="row" spacing={2}>
                            <Typography fontFamily="monospace">
                              1.70
                            </Typography>
                            <Chip label="X" color="error" size="small" />
                            <Chip label="P" color="primary" size="small" />
                            <Chip label="O" color="success" size="small" />
                          </Stack>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 3: Tipografía */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 180 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Encabezado
                    </Typography>
                    <TextField
                      label="Tamaño (px)"
                      type="number"
                      value={headerFontSize}
                      onChange={(e) => setHeaderFontSize(parseInt(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 10 }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <Typography sx={{ fontSize: `${headerFontSize}px` }}>
                        Ejemplo
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 180 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Posición
                    </Typography>
                    <TextField
                      label="Tamaño (px)"
                      type="number"
                      value={positionFontSize}
                      onChange={(e) => setPositionFontSize(parseInt(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 10 }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <Typography sx={{ fontSize: `${positionFontSize}px`, fontWeight: 'bold' }}>
                        1
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 180 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Resultado
                    </Typography>
                    <TextField
                      label="Tamaño (px)"
                      type="number"
                      value={resultFontSize}
                      onChange={(e) => setResultFontSize(parseInt(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 10 }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <Typography sx={{ fontSize: `${resultFontSize}px`, fontFamily: 'monospace' }}>
                        {eventType === 'horizontal' ? '7.25' : '1.70'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 180 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Nombre del Atleta
                    </Typography>
                    <TextField
                      label="Tamaño (px)"
                      type="number"
                      value={nameFontSize}
                      onChange={(e) => setNameFontSize(parseInt(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 10 }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <Typography sx={{ fontSize: `${nameFontSize}px` }}>
                        Nombre
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 180 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Equipo
                    </Typography>
                    <TextField
                      label="Tamaño (px)"
                      type="number"
                      value={teamFontSize}
                      onChange={(e) => setTeamFontSize(parseInt(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 10 }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <Typography sx={{ fontSize: `${teamFontSize}px`, opacity: 0.8 }}>
                        Equipo
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 180 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      {eventType === 'horizontal' ? 'Número de Intento' : 'Intentos'}
                    </Typography>
                    <TextField
                      label="Tamaño (px)"
                      type="number"
                      value={attemptFontSize}
                      onChange={(e) => setAttemptFontSize(parseInt(e.target.value) || 0)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 10 }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ pt: 1, pb: 1 }}>
                      {eventType === 'horizontal' ? (
                        <Typography sx={{ fontSize: `${attemptFontSize}px` }}>
                          Intento #1
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={2}>
                          <Typography sx={{ fontSize: `${attemptFontSize}px`, color: failColor }}>X</Typography>
                          <Typography sx={{ fontSize: `${attemptFontSize}px`, color: passColor }}>P</Typography>
                          <Typography sx={{ fontSize: `${attemptFontSize}px`, color: successColor }}>O</Typography>
                        </Stack>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 4: Colores */}
            <TabPanel value={activeTab} index={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Fondo del Tablero
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 80, 
                        backgroundColor: tableBackgroundColor,
                        borderRadius: 1,
                        mb: 2,
                        border: '1px solid rgba(0,0,0,0.1)'
                      }} 
                    />
                    <TextField
                      type="color"
                      value={tableBackgroundColor}
                      onChange={(e) => setTableBackgroundColor(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Encabezado
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 80, 
                        backgroundColor: headerColor,
                        borderRadius: 1,
                        mb: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }} 
                    >
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: headerFontColor,
                          fontWeight: 'bold'
                        }}
                      >
                        Evento
                      </Typography>
                    </Box>
                    <TextField
                      type="color"
                      value={headerColor}
                      onChange={(e) => setHeaderColor(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                      label="Color de Fondo"
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Posición
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 80, 
                        backgroundColor: positionColor,
                        borderRadius: 1,
                        mb: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }} 
                    >
                      <Typography
                        sx={{
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          color: headerFontColor,
                        }}
                      >
                        1
                      </Typography>
                    </Box>
                    <TextField
                      type="color"
                      value={positionColor}
                      onChange={(e) => setPositionColor(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Nombre
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 80, 
                        backgroundColor: nameBackgroundColor,
                        borderRadius: 1,
                        mb: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: '0 10px',
                      }} 
                    >
                      <Typography 
                        sx={{ 
                          color: athleteNameColor,
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                        }}
                      >
                        Nombre
                      </Typography>
                      <Typography 
                        sx={{ 
                          color: teamNameColor,
                          fontSize: '1rem',
                        }}
                      >
                        Equipo
                      </Typography>
                    </Box>
                    <TextField
                      type="color"
                      value={nameBackgroundColor}
                      onChange={(e) => setNameBackgroundColor(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {eventType === 'horizontal' ? 'Marca' : 'Altura'}
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 80, 
                        backgroundColor: markBackgroundColor,
                        borderRadius: 1,
                        mb: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }} 
                    >
                      <Typography 
                        sx={{ 
                          color: resultColor,
                          fontWeight: 'bold',
                          fontSize: '1.5rem',
                        }}
                      >
                        {eventType === 'horizontal' ? '7.25' : '1.70'}
                      </Typography>
                    </Box>
                    <TextField
                      type="color"
                      value={markBackgroundColor}
                      onChange={(e) => setMarkBackgroundColor(e.target.value)}
                      variant="outlined"
                      fullWidth
                      size="small"
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Color de Texto
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            backgroundColor: headerFontColor,
                            border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                        <Typography variant="caption">Encabezados</Typography>
                        <TextField
                          type="color"
                          size="small"
                          value={headerFontColor}
                          onChange={(e) => setHeaderFontColor(e.target.value)}
                          sx={{ width: '50%', ml: 'auto' }}
                        />
                      </Stack>
                      
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            backgroundColor: athleteNameColor,
                            border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                        <Typography variant="caption">Nombres</Typography>
                        <TextField
                          type="color"
                          size="small"
                          value={athleteNameColor}
                          onChange={(e) => setAthleteNameColor(e.target.value)}
                          sx={{ width: '50%', ml: 'auto' }}
                        />
                      </Stack>
                      
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            backgroundColor: teamNameColor,
                            border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                        <Typography variant="caption">Equipos</Typography>
                        <TextField
                          type="color"
                          size="small"
                          value={teamNameColor}
                          onChange={(e) => setTeamNameColor(e.target.value)}
                          sx={{ width: '50%', ml: 'auto' }}
                        />
                      </Stack>
                      
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            backgroundColor: resultColor,
                            border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                        <Typography variant="caption">Resultados</Typography>
                        <TextField
                          type="color"
                          size="small"
                          value={resultColor}
                          onChange={(e) => setResultColor(e.target.value)}
                          sx={{ width: '50%', ml: 'auto' }}
                        />
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
                
                {eventType === 'vertical' && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 200 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Colores de Intentos (Vertical)
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: failColor }}>X</Typography>
                          <Typography variant="body2">Fallido</Typography>
                          <TextField
                            type="color"
                            size="small"
                            value={failColor}
                            onChange={(e) => setFailColor(e.target.value)}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: passColor }}>P</Typography>
                          <Typography variant="body2">Pasa</Typography>
                          <TextField
                            type="color"
                            size="small"
                            value={passColor}
                            onChange={(e) => setPassColor(e.target.value)}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: successColor }}>O</Typography>
                          <Typography variant="body2">Exitoso</Typography>
                          <TextField
                            type="color"
                            size="small"
                            value={successColor}
                            onChange={(e) => setSuccessColor(e.target.value)}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* Tab 5: Temas */}
            <TabPanel value={activeTab} index={4}>
              <Typography variant="subtitle1" gutterBottom>
                Temas Predefinidos
              </Typography>
              <Grid container spacing={2}>
                {Object.keys(themes).map((themeName) => (
                  <Grid item xs={6} sm={3} key={themeName}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => applyTheme(themeName)}
                      sx={{
                        backgroundColor: themes[themeName].headerColor,
                        color: themes[themeName].headerFontColor,
                        textTransform: 'capitalize',
                        height: '80px',
                        '&:hover': {
                          backgroundColor: themes[themeName].headerColor,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {themeName}
                    </Button>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Previsualización del Tema Actual
                </Typography>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ 
                    bgcolor: headerColor, 
                    p: 1.5, 
                    color: headerFontColor,
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}>
                    {eventType === 'horizontal' ? 'Boys Shot Put' : 'Boys High Jump'}
                  </Box>
                  <Box sx={{ display: 'flex', height: '60px' }}>
                    <Box sx={{ 
                      width: '15%', 
                      bgcolor: positionColor, 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: headerFontColor, fontWeight: 'bold' }}>1</Typography>
                    </Box>
                    <Box sx={{ 
                      width: '45%', 
                      bgcolor: nameBackgroundColor, 
                      p: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}>
                      <Typography sx={{ color: athleteNameColor, fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Atleta Ejemplo
                      </Typography>
                      <Typography sx={{ color: teamNameColor, fontSize: '0.7rem' }}>
                        Universidad
                      </Typography>
                    </Box>
                    {eventType === 'horizontal' ? (
                      <Box sx={{ 
                        width: '40%', 
                        bgcolor: markBackgroundColor, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography sx={{ color: resultColor, fontWeight: 'bold' }}>
                          32-00.50
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ width: '40%', display: 'flex' }}>
                        <Box sx={{ 
                          width: '40%', 
                          bgcolor: markBackgroundColor, 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography sx={{ color: resultColor, fontWeight: 'bold' }}>
                            1.70
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: '20%', 
                          bgcolor: failColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>X</Typography>
                        </Box>
                        <Box sx={{ 
                          width: '20%', 
                          bgcolor: passColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>P</Typography>
                        </Box>
                        <Box sx={{ 
                          width: '20%', 
                          bgcolor: successColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>O</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 6: Presets de Configuración */}
            <TabPanel value={activeTab} index={5}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Cargar Configuración
                    </Typography>
                    
                    {configurations.filter(config => config.type === 'field' || 
                                                    config.type === 'field_horizontal' || 
                                                    config.type === 'field_vertical').length > 0 ? (
                      <>
                        <InputLabel id="select-config-label" sx={{ mb: 1 }}>
                          Seleccionar Preset Guardado
                        </InputLabel>
                        <Select
                          labelId="select-config-label"
                          value={selectedConfig}
                          onChange={(e) => {
                            setSelectedConfig(e.target.value);
                            loadConfiguration(e.target.value);
                          }}
                          fullWidth
                          size="small"
                          sx={{ mb: 2 }}
                        >
                          <MenuItem value="">
                            <em>Ninguno</em>
                          </MenuItem>
                          {configurations
                            .filter(config => config.type === 'field' || 
                                            config.type === 'field_horizontal' || 
                                            config.type === 'field_vertical')
                            .map((config) => (
                              <MenuItem key={config.id} value={config.id}>
                                {config.name} 
                                {config.type === 'field_horizontal' ? ' (Horizontal)' : 
                                 config.type === 'field_vertical' ? ' (Vertical)' : ''}
                              </MenuItem>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No hay configuraciones de campo guardadas
                      </Alert>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                      Guardar Configuración Actual
                    </Typography>
                    <TextField
                      label="Nombre de la configuración"
                      value={configName}
                      onChange={(e) => setConfigName(e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={saveConfiguration}
                      disabled={!configName.trim()}
                      fullWidth
                    >
                      Guardar Configuración
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </Card>
        </Grid>

        {/* Previsualización del tablero */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, overflow: 'hidden', mt: 3 }}>
            <CardHeader 
              title="Previsualización del Tablero" 
              sx={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}
              action={
                <Button
                  startIcon={<OpenInNewIcon />}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={openCurrentTablero}
                >
                  Abrir en Nueva Ventana
                </Button>
              }
            />
            <CardContent sx={{ p: 0, maxHeight: '600px', overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
              {eventType === 'horizontal' ? (
                <HorizontalEventTable
                  eventName={eventDetails.eventName}
                  eventSubtitle={eventDetails.eventSubtitle}
                  athleteData={{
                    position: "1",
                    name: "Constantine Tagliaferro",
                    team: "University of Wisconsin",
                    mark: "32-00.50",
                    attemptNumber: "1"
                  }}
                  displayRows={displayRows}
                  tableWidth={tableWidth}
                  tableHeight={Math.min(600, tableHeight)}
                  tableBackgroundColor={tableBackgroundColor}
                  headerColor={headerColor}
                  positionColor={positionColor}
                  nameBackgroundColor={nameBackgroundColor}
                  markBackgroundColor={markBackgroundColor}
                  attemptBackgroundColor={passColor}
                  headerFontColor={headerFontColor}
                  athleteNameColor={athleteNameColor}
                  teamNameColor={teamNameColor}
                  resultColor={resultColor}
                  attemptColor={headerFontColor}
                  headerFontSize={headerFontSize}
                  positionFontSize={positionFontSize}
                  nameFontSize={nameFontSize}
                  teamFontSize={teamFontSize}
                  resultFontSize={resultFontSize}
                  attemptFontSize={attemptFontSize}
                  showAttemptNumber={showAttemptNumber}
                  
                />
              ) : (
                <VerticalEventTable
                  eventName={eventDetails.eventName}
                  eventSubtitle={eventDetails.eventSubtitle}
                  athleteData={{
                    position: "1",
                    name: "Constantine Tagliaferro",
                    team: "University of Wisconsin",
                    height: "1.70",
                    attempts: ["X", "P", "O"]
                  }}
                  displayRows={displayRows}
                  tableWidth={tableWidth}
                  tableHeight={Math.min(600, tableHeight)}
                  tableBackgroundColor={tableBackgroundColor}
                  headerColor={headerColor}
                  positionColor={positionColor}
                  nameBackgroundColor={nameBackgroundColor}
                  heightBackgroundColor={markBackgroundColor}
                  headerFontColor={headerFontColor}
                  athleteNameColor={athleteNameColor}
                  teamNameColor={teamNameColor}
                  heightColor={resultColor}
                  failColor={failColor}
                  passColor={passColor}
                  successColor={successColor}
                  headerFontSize={headerFontSize}
                  positionFontSize={positionFontSize}
                  nameFontSize={nameFontSize}
                  teamFontSize={teamFontSize}
                  heightFontSize={resultFontSize}
                  attemptFontSize={attemptFontSize}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar para feedback visual */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TableroFieldEvents;